import {
  useCallback, useEffect, useRef, useState,
} from 'react';

/**
 * Observes an element's content-box width via ResizeObserver.
 *
 * Returns [setRef, width]:
 *   - setRef: a *callback ref* — pass it (or compose it) onto the element you
 *     want to measure. Using a callback ref removes any ambiguity about when
 *     the node is available; we (re)attach the observer the moment React hands
 *     us the node.
 *   - width: observed content-box width in px, or null until first measure.
 */
export default function useContainerWidth() {
  const [width, setWidth] = useState(null);
  const observerRef = useRef(null);
  const nodeRef = useRef(null);

  const setRef = useCallback((node) => {
    // Tear down any previous observation.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    nodeRef.current = node;
    if (!node) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      // jsdom / very old browsers: one-time measurement.
      const w = node.getBoundingClientRect().width;
      setWidth(w);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    observerRef.current = observer;

    // Seed an immediate measurement so we don't wait for the first resize tick.
    const seed = node.getBoundingClientRect().width;
    // TEMP debug — remove after verifying.
    setWidth(seed);
  }, []);

  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);

  return [setRef, width];
}
